export type VectorStore = "pinecone" | "s3vectors";
export type AppScreen = "input" | "live" | "results";
export type RunStatus = "idle" | "connecting" | "running" | "complete" | "error" | "cancelled";
export type PhaseStatus = "pending" | "active" | "done" | "error";

export interface Department {
  id: string;
  label: string;
  description?: string;
  suggested_questions?: string[];
  pinecone_index?: string;
}

export const PHASE_LABELS = [
  "Rewrite",
  "Classify",
  "Specialists",
  "Retrieve",
  "Synthesize",
] as const;

export interface PicotSpice {
  P?: string;
  I?: string;
  C?: string;
  O?: string;
  T?: string;
  S?: string;
  E?: string;
}

export interface Paper {
  title?: string;
  pmcid?: string;
  pmc_url?: string;
  doi?: string;
  summary?: string;
  rcs_score?: number;
  evidence_level?: string;
  citation_count?: number | null;
  journal?: string;
  journal_quality?: number | null;
  year?: number | null;
  final_score?: number;
  final_score_components?: Record<string, number>;
  is_retracted?: boolean;
}

export interface Consensus {
  agreement: string[];
  debate: string[];
  emerging: string[];
}

export interface RunManifest {
  run_id?: string;
  query?: string;
  rewritten_query?: string;
  framework?: "picot" | "spice" | string;
  picot?: PicotSpice;
  spice?: PicotSpice;
  specialists_used?: string[];
  vector_backend?: string;
  model_id?: string;
  model_provider?: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number;
  paper_count?: number;
  cci?: Record<string, number>;
  department?: string;
  department_label?: string;
  pinecone_index?: string;
}

export interface RunSummary {
  run_id: string;
  query: string;
  started_at?: string;
  completed_at?: string;
  duration_ms?: number | null;
  paper_count?: number;
  specialists_used?: string[];
  framework?: string;
  department?: string;
  department_label?: string;
}

export interface SavedRun {
  manifest: RunManifest;
  papers: { run_id?: string; papers: Paper[] };
  answer: {
    run_id?: string;
    answer?: string;
    consensus?: Consensus | null;
    followup_questions?: string[];
  };
}

export interface LogRow {
  id: string;
  timestamp: string;
  type: string;
  summary: string;
  tone: "default" | "success" | "warning" | "error";
  toolId?: string;
  done?: boolean;
}

export interface SpecialistState {
  key: string;
  paperCount: number | null;
  lastTool: string | null;
  text: string;
}

export interface SseEvent {
  type: string;
  data?: unknown;
  id?: string;
  name?: string;
  input?: unknown;
  papers?: Paper[];
  specialist?: string;
  tool?: string;
  count?: number;
}

export interface RunState {
  screen: AppScreen;
  prompt: string;
  vectorStore: VectorStore;
  defaultVectorStore: VectorStore;
  runId: string | null;
  runStatus: RunStatus;
  errorMessage: string | null;
  warningMessage: string | null;
  runStartMs: number | null;
  durationMs: number | null;
  paperCount: number;
  papers: Paper[];
  framework: string | null;
  rewrittenQuery: string | null;
  picot: PicotSpice | null;
  spice: PicotSpice | null;
  detectedDomains: string[];
  complexity: string | null;
  patientContext: string | null;
  phaseStatus: PhaseStatus[];
  phaseStartedAt: (number | null)[];
  phaseElapsedMs: (number | null)[];
  phase3WasActive: boolean;
  supervisorAnswer: string;
  reasoning: string;
  specialistText: Record<string, SpecialistState>;
  consensus: Consensus | null;
  followups: string[];
  cci: Record<string, number> | null;
  specialistsUsed: string[];
  manifest: RunManifest | null;
  log: LogRow[];
  logEventCount: number;
  activeTool: string | null;
  lastEventMs: number | null;
  showLiveLogOverlay: boolean;
  modelId: string | null;
  departmentId: string;
  departmentLabel: string;
  departments: Department[];
}

export const initialRunState = (): RunState => ({
  screen: "input",
  prompt: "",
  vectorStore: "pinecone",
  defaultVectorStore: "pinecone",
  runId: null,
  runStatus: "idle",
  errorMessage: null,
  warningMessage: null,
  runStartMs: null,
  durationMs: null,
  paperCount: 0,
  papers: [],
  framework: null,
  rewrittenQuery: null,
  picot: null,
  spice: null,
  detectedDomains: [],
  complexity: null,
  patientContext: null,
  phaseStatus: PHASE_LABELS.map(() => "pending"),
  phaseStartedAt: PHASE_LABELS.map(() => null),
  phaseElapsedMs: PHASE_LABELS.map(() => null),
  phase3WasActive: false,
  supervisorAnswer: "",
  reasoning: "",
  specialistText: {},
  consensus: null,
  followups: [],
  cci: null,
  specialistsUsed: [],
  manifest: null,
  log: [],
  logEventCount: 0,
  activeTool: null,
  lastEventMs: null,
  showLiveLogOverlay: false,
  modelId: null,
  departmentId: "plastic_surgery",
  departmentLabel: "Plastic Surgery",
  departments: [],
});
